import { useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * A sortable column header, and the hook that does the sorting.
 *
 * Every register in this product was fixed in one order — trainings by date descending,
 * employees by the order they were created — and an auditor reading a register sorts it
 * constantly: by date to find a period, by hours to find who is short, by name to find a
 * person. There was no way to do any of that.
 *
 * Sorted on the client, deliberately, for the same reason `usePaged` pages on the client:
 * these screens filter across the whole set on the server and then hold it, so sorting
 * here cannot disagree with what is on screen. Move it to SQL only when the set stops
 * fitting in memory, and move the paging with it.
 */

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/**
 * `columns` maps a key to how that column is read off a row: a field name, or a function.
 * A value of null/undefined/"" always sorts last, whichever direction is chosen — an
 * empty cell is not "smallest", it is absent, and burying it under a date is wrong.
 */
export function useSorted(rows, columns, sort) {
  return useMemo(() => {
    if (!sort) return rows;
    const [key, dir] = sort.split(":");
    const read = columns[key];
    if (!read) return rows;
    const get = typeof read === "function" ? read : (r) => r[read];
    const sign = dir === "desc" ? -1 : 1;
    // A copy: sorting the array the caller handed us mutates their state in place.
    return [...rows].sort((a, b) => {
      const x = get(a), y = get(b);
      const xEmpty = x === null || x === undefined || x === "";
      const yEmpty = y === null || y === undefined || y === "";
      if (xEmpty || yEmpty) return xEmpty && yEmpty ? 0 : xEmpty ? 1 : -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * sign;
      return collator.compare(String(x), String(y)) * sign;
    });
  }, [rows, columns, sort]);
}

/** Off -> ascending -> descending -> off. The third press is how you get back. */
export function nextSort(current, key) {
  const [k, dir] = (current || "").split(":");
  if (k !== key) return `${key}:asc`;
  if (dir === "asc") return `${key}:desc`;
  return "";
}

export default function SortableTh({ sortKey, sort, onSort, children, className, align = "left" }) {
  const [k, dir] = (sort || "").split(":");
  const active = k === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className={cn("px-4 py-3 font-medium", align === "right" && "text-right", className)}
        aria-sort={!active ? "none" : dir === "asc" ? "ascending" : "descending"}>
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, sortKey))}
        data-testid={`sort-${sortKey}`}
        className={cn(
          "inline-flex items-center gap-1.5 rounded-sm -mx-1 px-1 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          align === "right" && "flex-row-reverse",
          active ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {children}
        {/* Always rendered, never only on hover: a control that appears on hover cannot
            be found by anyone who is not already looking for it, and never on a touch
            screen at all. */}
        <Icon className={cn("w-3.5 h-3.5 shrink-0", active ? "opacity-100" : "opacity-40")}
              aria-hidden="true" />
      </button>
    </th>
  );
}
